import React from 'react';
import { useMemberExperienceAuth } from '@guildeducationinc/auth-frontend-tools';
import { Button } from '@guildeducationinc/recess';
import { GetMemberProgramQuery } from 'src/graphqlTypes/catalogServiceTypes';
import { trackConversion } from 'src/services/analytics';
import { applyLink, generateSponsorshipApplyLink } from 'src/utilities/urlUtility';
import { useHits } from 'react-instantsearch-hooks-web';
import { useSearchContext } from '@src/components/Providers/SearchProvider';
import { isAffiliateProgram } from '@src/utilities/offeringHitsUtility';
import { PlanProgramStatus } from '@src/constants/planProgramStatus';

interface Props {
	isDeferred?: boolean;
	program: GetMemberProgramQuery['memberProgram'];
}

export const StartApplicationButton: React.FC<Props> = ({ isDeferred, program }) => {
	const { userFromIdToken: authUser } = useMemberExperienceAuth();
	const { results } = useHits();
	const { planUuid } = useSearchContext();

	if (!program) {
		return null;
	}

	const url = new URL(window.location.href);
	const searchParams = url.searchParams;
	const queryID = searchParams.get('queryID');
	const isCapped = program.planProgramStatus === PlanProgramStatus.CAPPED;

	const handleAnalytics = () => {
		trackConversion({
			index: results._rawResults[0].indexUsed,
			objectId: `${program.id}-${planUuid}`,
			programId: program.id,
			queryId: queryID,
			segmentEventName: 'ClickApplicationStart',
			userUuid: authUser.id,
		});
	};

	const buttonText = () => {
		if (isDeferred) return 'Apply for next start date';
		if (isAffiliateProgram(program)) return 'Apply for sponsorship';
		return 'Start Guild application';
	};

	const isDisabled = (!program.nextDate && !isAffiliateProgram(program)) || isCapped;

	const startApplicationUrl = isAffiliateProgram(program)
		? generateSponsorshipApplyLink(program.studentFacingAcademicPartner.uuid)
		: applyLink(program);

	return (
		<Button
			disabled={isDisabled}
			fullWidth={true}
			href={isDisabled ? null : startApplicationUrl}
			onClick={() => handleAnalytics()}
			testid='start-application-button'
		>
			{buttonText()}
		</Button>
	);
};
